import React, { useEffect, useRef, useState } from "react";
import { TimelineStep } from "../../../types/timeline";
import { sanitizeStep } from "../../../utils/timelineValidation";

const LABEL_STYLE: React.CSSProperties = {
  display: "grid",
  gap: 6,
  fontSize: 10,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.5)",
};

const INPUT_STYLE: React.CSSProperties = {
  height: 36,
  borderRadius: 8,
  border: "1px solid rgba(255,255,255,0.1)",
  background: "rgba(9, 13, 24, 0.78)",
  color: "rgba(255,255,255,0.95)",
  paddingInline: 10,
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
};

const MONOSPACE_FONT =
  "'JetBrains Mono', 'Roboto Mono', 'SFMono-Regular', Consolas, monospace";

interface StepInspectorProps {
  selectedStep: TimelineStep | null;
  totalFrames: number;
  autoFocusToken: number;
  onSave: (step: TimelineStep) => void;
  onDelete: (stepId: string) => void;
  disabled?: boolean;
  emptyStateLabel: string;
  stepNameLabel: string;
  stepTextLabel: string;
  stepTextPlaceholder: string;
  startFrameLabel: string;
  endFrameLabel: string;
  saveStepLabel: string;
  deleteStepLabel: string;
  untitledStepLabel: string;
  idLabel: string;
}

export const StepInspector = React.memo(function StepInspector({
  selectedStep,
  totalFrames,
  autoFocusToken,
  onSave,
  onDelete,
  disabled = false,
  emptyStateLabel,
  stepNameLabel,
  stepTextLabel,
  stepTextPlaceholder,
  startFrameLabel,
  endFrameLabel,
  saveStepLabel,
  deleteStepLabel,
  untitledStepLabel,
  idLabel,
}: StepInspectorProps) {
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const [draftStep, setDraftStep] = useState<TimelineStep | null>(selectedStep);

  useEffect(() => {
    setDraftStep(selectedStep);
  }, [selectedStep]);

  useEffect(() => {
    if (selectedStep && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [selectedStep?.id, autoFocusToken]);

  if (!selectedStep || !draftStep) {
    return (
      <div
        className="timeline-step-inspector"
        style={{
          color: "rgba(255,255,255,0.65)",
          fontSize: 13,
          padding: "0.25rem 0",
        }}
        data-oid="-w1hdvy"
      >
        {emptyStateLabel}
      </div>
    );
  }

  return (
    <div
      className="timeline-step-inspector"
      style={{ display: "grid", gap: 14 }}
      data-oid="izl2z7a"
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "minmax(0, 7fr) minmax(88px, 1.5fr) minmax(88px, 1.5fr)",
          gap: 12,
          alignItems: "end",
        }}
        data-oid="s8:inx9"
      >
        <label style={LABEL_STYLE} data-oid="r2mbvh6">
          {stepNameLabel}
          <input
            ref={nameInputRef}
            type="text"
            value={draftStep.name}
            aria-label={stepNameLabel}
            disabled={disabled}
            onChange={(e) =>
              setDraftStep({
                ...draftStep,
                name: e.target.value || untitledStepLabel,
              })
            }
            style={INPUT_STYLE}
            data-oid="w-gdqij"
          />
        </label>

        <label style={LABEL_STYLE} data-oid="zbe-_y2">
          {startFrameLabel}
          <input
            type="number"
            value={draftStep.startFrame}
            aria-label={startFrameLabel}
            disabled={disabled}
            onChange={(e) =>
              setDraftStep(
                sanitizeStep(
                  {
                    ...draftStep,
                    startFrame: Number.isFinite(Number(e.target.value))
                      ? Number(e.target.value)
                      : draftStep.startFrame,
                  },
                  totalFrames,
                ),
              )
            }
            style={{
              ...INPUT_STYLE,
              fontFamily: MONOSPACE_FONT,
            }}
            data-oid="sn39xbq"
          />
        </label>

        <label style={LABEL_STYLE} data-oid=".vo:oh5">
          {endFrameLabel}
          <input
            type="number"
            value={draftStep.endFrame}
            aria-label={endFrameLabel}
            disabled={disabled}
            onChange={(e) =>
              setDraftStep(
                sanitizeStep(
                  {
                    ...draftStep,
                    endFrame: Number.isFinite(Number(e.target.value))
                      ? Number(e.target.value)
                      : draftStep.endFrame,
                  },
                  totalFrames,
                ),
              )
            }
            style={{
              ...INPUT_STYLE,
              fontFamily: MONOSPACE_FONT,
            }}
            data-oid="xqtmecp"
          />
        </label>
      </div>

      <label style={LABEL_STYLE} data-oid="k:dtq02">
        {stepTextLabel}
        <textarea
          value={draftStep.stepText ?? ""}
          aria-label={stepTextLabel}
          disabled={disabled}
          onChange={(e) =>
            setDraftStep({ ...draftStep, stepText: e.target.value })
          }
          placeholder={stepTextPlaceholder}
          rows={3}
          style={{
            minHeight: 72,
            borderRadius: 8,
            border: "1px solid rgba(255,255,255,0.1)",
            background: "rgba(9, 13, 24, 0.78)",
            color: "rgba(255,255,255,0.95)",
            padding: "10px 12px",
            resize: "vertical",
            fontFamily: "inherit",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
          }}
          data-oid="li0az-o"
        />
      </label>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
        }}
        data-oid="tge0u24"
      >
        <div
          style={{
            fontSize: 11,
            color: "rgba(255,255,255,0.48)",
            fontFamily: MONOSPACE_FONT,
          }}
          data-oid="3kw9y.2"
        >
          {selectedStep.id}
        </div>
        <div
          style={{ display: "flex", alignItems: "center", gap: 8 }}
          data-oid="ffxslzf"
        >
          <button
            type="button"
            onClick={() => onSave(sanitizeStep(draftStep, totalFrames))}
            disabled={disabled}
            style={{
              border: "1px solid rgba(56,189,248,0.36)",
              color: "rgba(240, 249, 255, 0.96)",
              background:
                "linear-gradient(135deg, rgba(56,189,248,0.85), rgba(14,165,233,0.75))",
              borderRadius: 8,
              padding: "7px 12px",
              boxShadow: "0 10px 22px rgba(2,132,199,0.24)",
              cursor: disabled ? "not-allowed" : "pointer",
            }}
            data-oid=".0pai7w"
          >
            {saveStepLabel}
          </button>

          <button
            type="button"
            onClick={() => onDelete(selectedStep.id)}
            disabled={disabled}
            style={{
              border: "1px solid rgba(244,63,94,0.45)",
              color: "rgb(253, 164, 175)",
              background: "transparent",
              borderRadius: 8,
              padding: "7px 12px",
              cursor: disabled ? "not-allowed" : "pointer",
            }}
            data-oid="ctia1rw"
          >
            {deleteStepLabel}
          </button>
        </div>
      </div>
    </div>
  );
});
